import { Path, Operation } from 'slate';
import { defineTransform, transform } from '../transform';

import {
  adjustPath,
  pathWasRemoved,
  moveNodeOpWithConsistentTargetPath,
  moveNodeOpWithSlateTargetPath,
  adjustNodePathForNodeMove,
  adjustPositionForNodeMove,
  adjustPathForNodeSplit,
  adjustPathForNodeMerge,
  adjustPathForNodeInsert,
  adjustPathForNodeRemove,
} from '../utils';

defineTransform('move_node', 'insert_text', (op1, op2, side) => op1);
defineTransform('move_node', 'remove_text', (op1, op2, side) => op1);
defineTransform('move_node', 'split_node', (op1, op2, side) => {
  if (Path.equals(op1.path, op1.newPath)) {
    // If the move isn't actually moving anything, it doesn't need
    // to exist.
    return;
  }

  let tOp = { ...op1 };

  tOp = moveNodeOpWithConsistentTargetPath(tOp);

  // Just like merge_node, we always want to move the first part of a
  // split. So we pass -1 for position, to lose the tiebreaker.
  tOp.path = adjustPathForNodeSplit(tOp.path, -1, op2, side === 'left');

  // There's an edge case right on the border of the split.
  //
  // Our "consistent node path" treats newPath as if the original node
  // has not been removed yet. But if the split is targeting the
  // original newPath, that will place the "consistent" newPath
  // _after_ the split -- even though it should just be swapping nodes
  // right before the split. I can't think of a way to handle this
  // without breaking the case where you insert after the split -- in
  // one case or the other, we'll have to make an adjustment.
  if (
    Path.isAncestor(op2.path, op1.path) &&
    op2.path.length === op1.newPath.length - 1 &&
    op1.newPath[op1.newPath.length - 1] < tOp.newPath[tOp.newPath.length - 1] &&
    tOp.newPath[tOp.newPath.length - 1] === op2.position
  ) {
    // Don't adjust the newPath, because it's actually happening
    // before the split -- we just adjusted it to be after the split.
  } else {
    // We also want to move _to_ the position before a split.
    tOp.newPath = adjustPathForNodeSplit(tOp.newPath, -1, op2, side === 'left');
  }

  tOp = moveNodeOpWithSlateTargetPath(tOp);

  // If we're moving the node we split, we want to move both parts, to
  // mirror the case where we split a moved node.
  if (Path.equals(op1.path, op2.path)) {
    const moveFirstPartOp = { ...tOp };
    let moveSecondPartOp = {
      ...tOp,
      path: adjustPath(tOp.path, 1, tOp.path.length - 1),
    };

    // We always want secondPart to lose the tiebreaker, because it's
    // the second part of our split, and we want its position to get
    // pushed over by the first part.
    moveSecondPartOp = transform(moveSecondPartOp, moveFirstPartOp, 'right');
    return [moveFirstPartOp, moveSecondPartOp];
  }

  return tOp;
});

defineTransform('move_node', 'merge_node', (op1, op2, side) => {
  const tOp = moveNodeOpWithConsistentTargetPath(op1);

  if (Path.equals(op1.path, op2.path)) {
    return [Operation.inverse(op2), op1];
  }

  tOp.path = adjustPathForNodeMerge(op1.path, op2);
  // We don't need to affect equal paths, because we're targeting a
  // position, not a node
  tOp.newPath = adjustPathForNodeMerge(op1.newPath, op2, false);

  return moveNodeOpWithSlateTargetPath(tOp);
});

defineTransform('move_node', 'move_node', (op1, op2, side) => {
  if (Path.equals(op2.path, op2.newPath)) {
    // The other move won't actually accomplish anything, so let's
    // shortcut it.
    return op1;
  }

  const tOp = moveNodeOpWithConsistentTargetPath(op1);
  if (Path.equals(op1.path, op2.path)) {
    if (side === 'right' && !Path.equals(op1.path, op1.newPath)) {
      const cOp2 = moveNodeOpWithConsistentTargetPath(op2);
      tOp.path = adjustPositionForNodeMove(cOp2.newPath, op2, false);
      tOp.newPath = adjustPositionForNodeMove(tOp.newPath, op2, false);
      return moveNodeOpWithSlateTargetPath(tOp);
    } else {
      // If both operations are moving the same node, one of them
      // has to COOL IT
      return;
    }
  }

  if (
    Path.isAncestor(op2.path, op1.newPath) &&
    Path.isAncestor(op1.path, op2.newPath)
  ) {
    // Each op is moving a node into a child of the other node to be
    // moved. One of them is going to have to lose.
    if (side === 'right') {
      return [Operation.inverse(op2), op1];
    } else {
      return;
    }
  }

  tOp.path = adjustNodePathForNodeMove(tOp.path, op2);

  // If path and newPath are the same at the beginning, they must end
  // up the same.
  if (Path.equals(op1.path, op1.newPath)) {
    tOp.newPath = tOp.path;
  } else {
    tOp.newPath = adjustPositionForNodeMove(tOp.newPath, op2, side === 'right');
  }

  return moveNodeOpWithSlateTargetPath(tOp);
});

defineTransform('move_node', 'insert_node', (op1, op2, side) => {
  const tOp = moveNodeOpWithConsistentTargetPath(op1);
  tOp.path = adjustPathForNodeInsert(tOp.path, op2, true);

  // If path and newPath are the same at the beginning, they must end
  // up the same.
  if (Path.equals(op1.path, op1.newPath)) {
    tOp.newPath = tOp.path;
  } else {
    tOp.newPath = adjustPathForNodeInsert(tOp.newPath, op2, side === 'right');
  }

  return moveNodeOpWithSlateTargetPath(tOp);
});

defineTransform('move_node', 'remove_node', (op1, op2, side) => {
  let tOp = { ...op1 };
  if (pathWasRemoved(op1.path, op2.path)) return;
  if (Path.isAncestor(op2.path, op1.newPath)) {
    const path = adjustPathForNodeRemove(op1.path, op2);
    const removeOp = {
      type: 'remove_node',
      path: path,
      // Placeholder, because we can't know anything about the
      // node. If it's important, it will get fixed up in a final pass
      // over the Value.
      node: { type: 'paragraph', children: [] },
    };

    return removeOp;
  }

  tOp = moveNodeOpWithConsistentTargetPath(tOp);
  tOp.path = adjustPathForNodeRemove(tOp.path, op2, true);
  tOp.newPath = adjustPathForNodeRemove(tOp.newPath, op2, false);
  return moveNodeOpWithSlateTargetPath(tOp);
});

defineTransform('move_node', 'set_node', (op1, op2, side) => op1);
defineTransform('move_node', 'add_mark', (op1, op2, side) => op1);
defineTransform('move_node', 'remove_mark', (op1, op2, side) => op1);
defineTransform('move_node', 'set_mark', (op1, op2, side) => op1);
defineTransform('move_node', 'add_annotation', (op1, op2, side) => op1);
defineTransform('move_node', 'remove_annotation', (op1, op2, side) => op1);
defineTransform('move_node', 'set_annotation', (op1, op2, side) => op1);
defineTransform('move_node', 'set_selection', (op1, op2, side) => op1);
defineTransform('move_node', 'add_metadata', (op1, op2, side) => op1);
defineTransform('move_node', 'remove_metadata', (op1, op2, side) => op1);
defineTransform('move_node', 'set_metadata', (op1, op2, side) => op1);
